<template>
  <b-modal
    :id="modalId"
    title="Add a job"
    size="md"
    centered
    hide-footer
    modal-class="job-chooser-shell"
    header-class="job-chooser-header"
    body-class="job-chooser-body"
    @shown="focusFirst"
  >
    <template #modal-title>
      <div class="job-chooser-title">Add a job</div>
      <div v-if="contextLabel" class="job-chooser-subtitle">
        {{ contextLabel }}
      </div>
    </template>

    <div class="job-chooser-choices">
      <button
        ref="single"
        type="button"
        class="job-chooser-choice"
        @click="choose('single')"
      >
        <span class="job-chooser-icon" aria-hidden="true">
          <b-icon-calendar-date />
        </span>
        <span class="job-chooser-heading">Single job</span>
        <span class="job-chooser-help">One date only</span>
      </button>

      <button
        type="button"
        class="job-chooser-choice"
        @click="choose('series')"
      >
        <span class="job-chooser-icon" aria-hidden="true">
          <b-icon-arrow-repeat />
        </span>
        <span class="job-chooser-heading">Repeating</span>
        <span class="job-chooser-help">Same job on chosen weekdays</span>
      </button>
    </div>

    <div class="job-chooser-footer">
      <b-button variant="link" size="sm" @click="hide">Cancel</b-button>
    </div>
  </b-modal>
</template>

<script>
import dayjs from 'dayjs'

// Step one of creating a job: pick single or repeating, then the form opens in
// that mode. Replaces the "Repeats" tickbox that used to sit halfway down the
// form, where the BA met it only after filling most of it in.
export default {
  name: 'JobTypeChooser',

  data() {
    return {
      modalId: 'job-type-chooser',
      prefill: {},
      workerName: '',
    }
  },

  computed: {
    // Names what was clicked, so the BA can see what they're adding to before
    // they commit to a shape. Absent when opened from the New Job button.
    contextLabel() {
      const parts = []
      if (this.prefill.date) {
        const date = dayjs(this.prefill.date)
        if (date.isValid()) parts.push(date.format('ddd D MMM'))
      }
      if (this.workerName) parts.push(this.workerName)
      return parts.join(' · ')
    },
  },

  methods: {
    // prefill carries the clicked cell's date and worker through to whichever
    // mode is picked; workerName is display-only.
    open(prefill = {}, workerName = '') {
      this.prefill = { ...prefill }
      this.workerName = workerName
      this.$bvModal.show(this.modalId)
    },

    hide() {
      this.$bvModal.hide(this.modalId)
    },

    focusFirst() {
      this.$nextTick(() => {
        if (this.$refs.single) this.$refs.single.focus()
      })
    },

    choose(mode) {
      this.hide()
      this.$emit('choose', { mode, prefill: { ...this.prefill } })
    },
  },
}
</script>

<style>
.job-chooser-shell .modal-content {
  border: 0;
  border-radius: 14px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
}

.job-chooser-header {
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.job-chooser-title {
  color: #111827;
  font-size: 1.1rem;
  font-weight: 700;
}

.job-chooser-subtitle {
  margin-top: 2px;
  color: #6b7280;
  font-size: 0.8125rem;
  font-weight: 400;
}

.job-chooser-body {
  padding: 1.25rem;
  background: #f9fafb;
}

.job-chooser-choices {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.job-chooser-choice {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 116px;
  padding: 1.25rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  text-align: center;
  cursor: pointer;
}

.job-chooser-choice:hover,
.job-chooser-choice:focus {
  border-color: #006cff;
  box-shadow: 0 12px 20px rgba(44, 102, 213, 0.4);
  outline: none;
}

.job-chooser-icon {
  color: #374151;
  font-size: 22px;
  line-height: 1;
}

.job-chooser-heading {
  margin-top: 0.65rem;
  color: #111827;
  font-weight: 700;
}

.job-chooser-help {
  margin-top: 0.15rem;
  color: #6b7280;
  font-size: 0.8125rem;
}

.job-chooser-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
}

@media (max-width: 600px) {
  .job-chooser-choices {
    grid-template-columns: 1fr;
  }
}
</style>
